/**
 * Screen 4 "Outputs" grouping — briefs on top, off-page variants nested under
 * each brief, plus the fire-summary PDF at the bottom.
 *
 * Wiring note: the API returns a `child_run_id` per step (see manual campaign
 * detail route). We link to the same detail pages the manual campaign flow
 * uses. When the underlying step exposes a rendered PDF URL, we surface a
 * separate download link.
 */
import { Link } from '@tanstack/react-router';
import { ExternalLink, FileText, Sparkles } from 'lucide-react';
import type { CampaignStep, Campaign } from '@seo/types';
import { Section, SectionHeader, SectionTitle } from '@/components/ui/section';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';

/**
 * The API's step `result` blob is untyped in @seo/types but often contains
 * a `pdf_url`. We probe for it defensively — anything else falls back to the
 * workspace link.
 */
function pdfUrlOf(step: CampaignStep): string | null {
  const r = step.result as Record<string, unknown> | null;
  if (!r) return null;
  const v = r['pdf_url'];
  return typeof v === 'string' ? v : null;
}

/**
 * The campaign row may carry a fire-summary PDF URL directly (the workflow
 * writes it there after composeBriefing). Not part of the shared Campaign
 * type — we probe defensively.
 */
function fireSummaryPdfOf(campaign: Campaign | undefined): string | null {
  const anyC = campaign as unknown as Record<string, unknown> | undefined;
  const v = anyC?.['fire_summary_pdf_url'] ?? anyC?.['fireSummaryPdfUrl'];
  return typeof v === 'string' ? v : null;
}

interface OutputLinkProps {
  step: CampaignStep;
  label: string;
  nested?: boolean;
}

function OutputLink({ step, label, nested }: OutputLinkProps): JSX.Element {
  const pdf = pdfUrlOf(step);
  const workspaceUrl = detailUrlFor(step);
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/60 bg-card/40 p-2.5 text-sm',
        nested && 'ml-6',
      )}
    >
      <div className="flex items-center gap-2 min-w-0">
        <FileText className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="truncate">{label}</span>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {workspaceUrl ? (
          <Button asChild size="sm" variant="ghost">
            <Link to={workspaceUrl.to} params={workspaceUrl.params}>
              <ExternalLink className="h-3 w-3" />
              View
            </Link>
          </Button>
        ) : step.step_type === 'a7_rank' ? (
          // Rank checks have no per-run detail page; point at Tracking so
          // the row is never a dead end when there's also no PDF.
          <Button asChild size="sm" variant="ghost">
            <Link to="/tracking">
              <ExternalLink className="h-3 w-3" />
              Open Tracking
            </Link>
          </Button>
        ) : null}
        {pdf ? (
          <Button asChild size="sm" variant="outline">
            <a href={pdf} target="_blank" rel="noreferrer">
              <FileText className="h-3 w-3" />
              PDF
            </a>
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Map a step type to the correct workspace detail route. Mirrors the switch
 * in `apps/web/src/routes/_app/campaigns/[id].tsx`. Not every step has a
 * workspace analog — those get `null` and the row shows only the PDF link.
 * Shared with `fire-timeline` so timeline rows link to the same places.
 */
export function detailUrlFor(
  step: CampaignStep,
): { to: string; params: Record<string, string> } | null {
  const id = step.child_run_id;
  if (!id) return null;
  switch (step.step_type) {
    case 'a1_audit':
      return { to: '/audits/$auditId', params: { auditId: id } };
    case 'a2_keyword':
      return { to: '/keywords/$keywordRunId', params: { keywordRunId: id } };
    case 'a3_brief':
      return { to: '/briefs/$briefId', params: { briefId: id } };
    case 'a5_competitor':
      return { to: '/competitors/$runId', params: { runId: id } };
    case 'a5_analysis':
      return { to: '/competitor-analyses/$analysisId', params: { analysisId: id } };
    case 'a6_calendar':
      return { to: '/calendars/$calendarId', params: { calendarId: id } };
    case 'a10_off_page':
      return {
        to: '/off-page-briefs/$offPageBriefId',
        params: { offPageBriefId: id },
      };
    default:
      return null;
  }
}

export interface OutputsTreeProps {
  campaign: Campaign | undefined;
  steps: CampaignStep[] | undefined;
}

export function OutputsTree({ campaign, steps }: OutputsTreeProps): JSX.Element {
  const rows = steps ?? [];
  const completed = rows.filter((s) => s.status === 'completed');
  // Foundation runs first — audit / keyword / calendar outputs used to be
  // silently dropped here even though `detailUrlFor` could link them.
  const audits = completed.filter((s) => s.step_type === 'a1_audit');
  const keywordRuns = completed.filter((s) => s.step_type === 'a2_keyword');
  const calendars = completed.filter((s) => s.step_type === 'a6_calendar');
  const briefs = completed.filter((s) => s.step_type === 'a3_brief');
  const competitor = completed.filter((s) => s.step_type === 'a5_competitor');
  const teardowns = completed.filter((s) => s.step_type === 'a5_analysis');
  const rank = completed.filter((s) => s.step_type === 'a7_rank');
  // A10 variants — batch or per-variant depending on workflow shape.
  const offPageVariants = completed.filter(
    (s) => s.step_type === 'a10_off_page',
  );
  // Group A10 variants by their parent brief when possible. The API doesn't
  // expose the parent link directly on the step row — we approximate by
  // matching sequence order (variants come after their brief in seq).
  function variantsForBrief(briefSeq: number): CampaignStep[] {
    const nextBrief = briefs.find((b) => b.seq > briefSeq);
    const upper = nextBrief?.seq ?? Number.POSITIVE_INFINITY;
    return offPageVariants.filter((v) => v.seq > briefSeq && v.seq < upper);
  }

  const fireSummaryPdf = fireSummaryPdfOf(campaign);

  if (completed.length === 0) {
    return (
      <Section>
        <SectionHeader>
          <SectionTitle>Outputs</SectionTitle>
        </SectionHeader>
        <p className="text-sm text-muted-foreground">
          Outputs appear here as steps complete.
        </p>
      </Section>
    );
  }

  return (
    <Section>
      <SectionHeader>
        <SectionTitle className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          Outputs
        </SectionTitle>
      </SectionHeader>

      <div className="space-y-2">
        {audits.map((a) => (
          <OutputLink key={a.id} step={a} label="Site audit" />
        ))}

        {keywordRuns.map((k) => (
          <OutputLink key={k.id} step={k} label="Keyword run" />
        ))}

        {competitor.map((c) => (
          <OutputLink key={c.id} step={c} label="Competitor digest" />
        ))}

        {teardowns.map((t) => (
          <OutputLink key={t.id} step={t} label={t.title} />
        ))}

        {briefs.map((b, i) => {
          const variants = variantsForBrief(b.seq);
          return (
            <div key={b.id} className="space-y-1.5">
              <OutputLink step={b} label={`Brief ${i + 1}: ${b.title}`} />
              {variants.map((v) => (
                <OutputLink key={v.id} step={v} label={v.title} nested />
              ))}
            </div>
          );
        })}

        {calendars.map((c) => (
          <OutputLink key={c.id} step={c} label="Content plan" />
        ))}

        {rank.map((r) => (
          <OutputLink key={r.id} step={r} label="Rank tracking snapshot" />
        ))}

        {fireSummaryPdf ? (
          <div className="flex items-center justify-between gap-3 rounded-md border border-primary/40 bg-primary/5 p-2.5 text-sm">
            <div className="flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              <span className="font-medium">Fire summary</span>
            </div>
            <Button asChild size="sm" variant="outline">
              <a href={fireSummaryPdf} target="_blank" rel="noreferrer">
                <FileText className="h-3 w-3" />
                Download master PDF
              </a>
            </Button>
          </div>
        ) : null}
      </div>
    </Section>
  );
}
